'use client';

import { Gift, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { cn } from '@/lib/cn';
import type { StoredReferralAttribution } from '@/types/referrals';

interface AuthReferralBannerProps {
  className?: string;
  attribution: StoredReferralAttribution | null;
  onDismiss?: () => void;
}

export function AuthReferralBanner({ className, attribution, onDismiss }: AuthReferralBannerProps) {
  const t = useTranslations('referrals.authBanner');
  const [dismissedCode, setDismissedCode] = useState<string | null>(null);

  if (!attribution || dismissedCode === attribution.referralCode) {
    return null;
  }

  const creatorName = attribution.creatorDisplayName || attribution.referralCode;
  const rewards = attribution.rewards;
  const unavailable = Boolean(attribution.referralStatus && attribution.referralStatus !== 'active');

  return (
    <div
      className={cn(
        'rounded-xl border border-[#4ABDE9]/25 bg-[#4ABDE9]/10 px-3.5 py-3 sm:px-4',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#4ABDE9]/15 text-[#177DA4] sm:h-9 sm:w-9">
            <Gift className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="text-[13px] font-semibold leading-5 text-hr-text-primary sm:text-sm">
              {unavailable ? t('unavailableTitle') : rewards
                ? t('giftTitle', { credits: rewards.invitee_signup_credits })
                : t('title', { name: creatorName })}
            </p>
            <p className="mt-1 text-xs leading-5 text-hr-text-secondary">
              {unavailable ? t('unavailableBody') : rewards
                ? t('giftBody', { name: creatorName, days: rewards.manual_bind_grace_days })
                : t('verificationPending')}
            </p>
            {!unavailable && rewards ? <p className="mt-1 text-xs leading-5 text-hr-text-secondary">{t('activationRule', { days: rewards.activation_window_days })}</p> : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setDismissedCode(attribution.referralCode);
            onDismiss?.();
          }}
          aria-label={t('dismiss')}
          className="shrink-0 rounded-lg p-1.5 text-hr-text-secondary transition-colors hover:bg-[#4ABDE9]/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4ABDE9]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
